import { TEMPO_DIVISION_INDEX, migratePresetModel } from "@defibrillator/preset-core";
import { SYNTH_VERSION, createInitVitalJson, customLfoShape, lfoShapeByName, mapParamValue, stepsToLfoShape, } from "./init-template.js";
import { MOD_DEST_TO_VITAL, MOD_SOURCE_TO_VITAL, PARAM_TO_VITAL } from "./param-map.js";
import { DEFAULT_VITAL_TARGET_VERSION, resolveVitalExportProfile, } from "./target-version.js";
import { generateBasic, harmonic, pulse, } from "./wavetable.js";
function mapLfoShape(shape) {
    return lfoShapeByName(shape);
}
function styleToVital(style) {
    switch (style) {
        case "bass":
            return "Bass";
        case "lead":
            return "Lead";
        case "pad":
            return "Pad";
        case "pluck":
            return "Keys";
        case "keys":
            return "Keys";
        case "fx":
            return "SFX";
        case "perc":
            return "Percussion";
        case "sequence":
            return "Sequence";
        case "other":
            return "Template";
    }
}
export function generateFromModel(gen) {
    switch (gen.kind) {
        case "basic":
            return generateBasic(gen.shape);
        case "pulse":
            return pulse(gen.width);
        case "harmonic":
            return harmonic(gen.harmonics);
    }
}
/** Wrap a generated frame into Vital WavetableCreator JSON. */
export function toVitalWavetable(generated, synthVersion = SYNTH_VERSION) {
    return {
        name: generated.name,
        author: generated.author,
        version: synthVersion,
        remove_all_dc: true,
        full_normalize: true,
        num_frames: generated.num_frames,
        num_samples: generated.num_samples,
        groups: [
            {
                components: [
                    {
                        type: "Wave Source",
                        interpolation_style: 1,
                        interpolation: 0,
                        keyframes: [
                            {
                                position: 0,
                                wave_data: generated.wave_data,
                            },
                        ],
                    },
                ],
            },
        ],
    };
}
function triggerToSyncType(trigger) {
    switch (trigger) {
        case "note_retrigger":
            return 0;
        case "sync":
            return 1;
        case "free":
            return 2;
        case "one_shot":
            return 3;
    }
}
function applyModulatorToSettings(settings, lfos, mod) {
    const i = mod.index;
    let shape;
    if (mod.shape.kind === "preset") {
        shape = mapLfoShape(mod.shape.shape);
    }
    else if (mod.shape.kind === "custom") {
        shape = customLfoShape(`Custom ${i}`, mod.shape.points, mod.shape.smooth);
    }
    else {
        shape = stepsToLfoShape(`Steps ${i}`, mod.shape.values, mod.shape.smoothing ?? 0);
    }
    lfos[i - 1] = shape;
    settings[`lfo_${i}_sync_type`] = triggerToSyncType(mod.trigger);
    settings[`lfo_${i}_phase`] = mod.phase;
    settings[`lfo_${i}_fade_time`] = mod.fadeSeconds;
    settings[`lfo_${i}_delay_time`] = mod.delaySeconds;
    settings[`lfo_${i}_stereo`] = mod.stereoOffset;
    if (mod.timing.mode === "tempo") {
        settings[`lfo_${i}_sync`] = 1; // tempo
        settings[`lfo_${i}_tempo`] = TEMPO_DIVISION_INDEX[mod.timing.division];
    }
    else {
        settings[`lfo_${i}_sync`] = 0; // time / Hz
        settings[`lfo_${i}_frequency`] = mapParamValue(`lfo${i}_frequency`, mod.timing.frequencyHz);
    }
}
/**
 * Build Vital JSON from an application PresetModel.
 * Browser-safe (no node:crypto) — use for client downloads.
 */
export function buildVitalJson(rawModel, options = {}) {
    const profile = resolveVitalExportProfile(options.targetVersion ?? DEFAULT_VITAL_TARGET_VERSION);
    const model = migratePresetModel(rawModel);
    const json = createInitVitalJson();
    json.preset_name = model.name;
    json.comments = model.comments;
    json.author = "Defibrillator";
    json.preset_style = styleToVital(model.style);
    json.synth_version = profile.synthVersion;
    const settings = json.settings;
    for (const [param, value] of Object.entries(model.params)) {
        if (param === "voice_mode")
            continue;
        if (param === "sub_destination_filter")
            continue;
        const key = PARAM_TO_VITAL[param];
        if (!key)
            continue;
        // Destination aliases share keys with filter inputs — prefer explicit filter_*_input.
        if (param === "osc1_destination_filter" ||
            param === "osc2_destination_filter" ||
            param === "osc3_destination_filter") {
            continue;
        }
        settings[key] = mapParamValue(param, value);
    }
    // Voice mode: polyphony + legato
    const voiceMode = Math.round(model.params.voice_mode);
    settings.polyphony = model.params.polyphony;
    settings.legato = voiceMode >= 2 ? 1 : 0;
    if (voiceMode === 1) {
        settings.polyphony = 1;
        settings.legato = 0;
    }
    else if (voiceMode === 2) {
        settings.polyphony = 1;
        settings.legato = 1;
    }
    settings.osc_1_on = (model.params.osc1_level ?? 0) > 0 ? 1 : 0;
    settings.osc_2_on = (model.params.osc2_level ?? 0) > 0 ? 1 : 0;
    settings.osc_3_on = (model.params.osc3_level ?? 0) > 0 ? 1 : 0;
    settings.filter_1_mix = model.params.filter_mix;
    settings.filter_1_osc1_input =
        model.params.filter_osc1_input >= 0.5 && model.params.osc1_destination_filter >= 0.5 ? 1 : 0;
    settings.filter_1_osc2_input =
        model.params.filter_osc2_input >= 0.5 && model.params.osc2_destination_filter >= 0.5 ? 1 : 0;
    settings.filter_1_osc3_input =
        model.params.filter_osc3_input >= 0.5 && model.params.osc3_destination_filter >= 0.5 ? 1 : 0;
    settings.filter_1_sample_input = model.params.filter_sample_input >= 0.5 ? 1 : 0;
    if (model.params.sub_on >= 0.5 && model.params.sub_destination_filter >= 0.5) {
        // Sub has its own path in Vital; keep filter sample input independent.
        settings.sub_on = 1;
    }
    // Delay sync to tempo when delay is on
    if (model.params.delay_on >= 0.5) {
        settings.delay_sync = 1;
        settings.delay_tempo = model.params.delay_tempo;
    }
    for (const slot of model.modulations) {
        const index = slot.slot - 1;
        if (index < 0 || index >= settings.modulations.length)
            continue;
        settings.modulations[index] = {
            source: MOD_SOURCE_TO_VITAL[slot.source],
            destination: MOD_DEST_TO_VITAL[slot.destination],
        };
        settings[`modulation_${slot.slot}_amount`] = slot.amount;
        settings[`modulation_${slot.slot}_bipolar`] = slot.bipolar ? 1 : 0;
    }
    // Macro assignments → additional modulation routes from macro sources
    for (const macro of model.macros) {
        const labelKey = `macro${macro.index}`;
        json[labelKey] = macro.name;
        for (const assignment of macro.assignments) {
            // Find a free modulation slot
            let free = -1;
            for (let s = 0; s < settings.modulations.length; s++) {
                if (!settings.modulations[s]?.source) {
                    free = s;
                    break;
                }
            }
            if (free < 0)
                break;
            const slotNum = free + 1;
            settings.modulations[free] = {
                source: MOD_SOURCE_TO_VITAL[`macro_${macro.index}`],
                destination: MOD_DEST_TO_VITAL[assignment.destination],
            };
            settings[`modulation_${slotNum}_amount`] = assignment.amount;
            settings[`modulation_${slotNum}_bipolar`] = assignment.bipolar ? 1 : 0;
        }
    }
    const lfos = [...settings.lfos];
    // Prefer full modulator state; fall back to legacy lfoShapes.
    if (model.modulators?.length) {
        for (const mod of model.modulators) {
            applyModulatorToSettings(settings, lfos, mod);
        }
    }
    else {
        for (const [key, shape] of Object.entries(model.lfoShapes)) {
            const index = Number(key) - 1;
            if (index >= 0 && index < lfos.length) {
                lfos[index] = mapLfoShape(shape);
            }
        }
    }
    settings.lfos = lfos;
    settings.wavetables = [1, 2, 3].map((osc) => {
        const gen = model.wavetables[osc];
        return toVitalWavetable(generateFromModel(gen), profile.synthVersion);
    });
    return json;
}
/** Emit `.vital` file text without content hashing (browser-safe). */
export function emitVitalText(model, options = {}) {
    return `${JSON.stringify(buildVitalJson(model, options), null, 2)}\n`;
}
//# sourceMappingURL=emit.js.map